import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Car, ArrowUpRight, Clock, MapPin } from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  assigned: "bg-blue-50 text-blue-700 border-blue-200",
  driver_en_route: "bg-primary-50 text-primary-700 border-primary-200",
  arrived: "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_progress: "bg-primary-50 text-primary-700 border-primary-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function MyRides() {
  const [tab, setTab] = useState("upcoming");
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/bookings").then((r) => setItems(r.data || []));
  }, []);

  const upcoming = items.filter((b) => !["completed", "cancelled"].includes(b.status));
  const past = items.filter((b) => ["completed", "cancelled"].includes(b.status));
  const data = tab === "upcoming" ? upcoming : past;

  return (
    <div className="min-h-screen bg-white">
      <div className="px-5 pt-safe pt-8 pb-3">
        <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">My Trips</p>
        <h1 className="font-display font-bold text-3xl text-slate-900">Your Journeys</h1>
      </div>

      <div className="px-5">
        <div className="flex gap-2 bg-slate-100 p-1 rounded-xl">
          <button
            data-testid="rides-tab-upcoming"
            onClick={() => setTab("upcoming")}
            className={`flex-1 h-10 rounded-lg font-semibold text-sm transition-all ${
              tab === "upcoming" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            Upcoming · {upcoming.length}
          </button>
          <button
            data-testid="rides-tab-past"
            onClick={() => setTab("past")}
            className={`flex-1 h-10 rounded-lg font-semibold text-sm transition-all ${
              tab === "past" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            Past · {past.length}
          </button>
        </div>
      </div>

      <div className="px-5 mt-5 space-y-3 pb-32">
        {data.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="rides-empty">
            <Car size={48} className="mx-auto opacity-40" />
            <p className="mt-3 font-semibold text-slate-500">No {tab} trips yet</p>
          </div>
        ) : (
          data.map((b) => {
            const Wrap = ["completed", "cancelled"].includes(b.status) ? "div" : Link;
            const props = Wrap === Link ? { to: `/app/track/${b.id}` } : {};
            return (
              <Wrap
                key={b.id}
                {...props}
                data-testid={`ride-card-${b.id}`}
                className="block bg-white border border-slate-200 rounded-2xl p-4 hover:border-slate-300 transition-all shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary flex items-center justify-center flex-shrink-0">
                    <Car size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-display font-bold text-slate-900 truncate">{b.dropoff_address}</p>
                      {Wrap === Link && <ArrowUpRight size={18} className="text-slate-300" />}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                      <MapPin size={12} /> from {b.pickup_address?.split(",")[0]}
                    </p>
                    <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${STATUS_BADGE[b.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {b.status.replace(/_/g, " ")}
                      </span>
                      <span className="text-[10px] text-slate-400 flex items-center gap-1 font-mono">
                        <Clock size={11} />
                        {new Date(b.created_at).toLocaleString("en-AE", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    </div>
                  </div>
                </div>
              </Wrap>
            );
          })
        )}
      </div>
    </div>
  );
}
